import { useCallback, useEffect, useRef, useState } from 'react'
import { uploadDropped } from '@/lib/files'

export type Attachment = {
  id: string
  file: File
  status: 'uploading' | 'done' | 'error'
  /** 0..1 of the bytes that have left the browser. */
  progress: number
  /** Where it landed in the session folder, once uploaded. */
  path?: string
  error?: string
}

/** Automatic retries after a failed upload, before the tile asks for a click. */
export const UPLOAD_RETRIES = 3

type Job = { ctrl: AbortController; done: Promise<string | null> }

/**
 * Files attached to the message being written. Each shows in the tray the
 * moment it is dropped and starts uploading to the session's `uploads/` in
 * the background, so sending only waits for whatever is still in flight.
 */
export function useAttachments(sessionId: string | undefined) {
  const [items, setItems] = useState<Attachment[]>([])
  const jobs = useRef(new Map<string, Job>())
  const latest = useRef(items)
  latest.current = items

  const patch = useCallback((id: string, p: Partial<Attachment>) => setItems((all) => all.map((a) => (a.id === id ? { ...a, ...p } : a))), [])

  const start = useCallback((id: string, file: File) => {
    if (!sessionId) return
    const ctrl = new AbortController()
    // A dropped socket fails an upload now and then; try again before asking the person.
    const attempt = async (): Promise<string | undefined> => {
      for (let n = 0; ; n++) {
        try {
          return (await uploadDropped(sessionId, [file], (_t, k) => patch(id, { progress: k }), ctrl.signal))[0]
        } catch (e) {
          if (ctrl.signal.aborted || n >= UPLOAD_RETRIES) throw e
          patch(id, { progress: 0 })
          await new Promise((r) => setTimeout(r, 1000 * 2 ** n))
          if (ctrl.signal.aborted) throw e
        }
      }
    }
    const done = attempt().then(
      (path) => { patch(id, { status: 'done', progress: 1, path }); return path ?? null },
      (e: Error) => { if (!ctrl.signal.aborted) patch(id, { status: 'error', error: e.message }); return null },
    )
    jobs.current.set(id, { ctrl, done })
  }, [sessionId, patch])

  const add = useCallback((files: File[]) => {
    const fresh = files.map((file) => ({ id: crypto.randomUUID(), file, status: 'uploading' as const, progress: 0 }))
    setItems((all) => [...all, ...fresh])
    for (const a of fresh) start(a.id, a.file)
  }, [start])

  const remove = useCallback((id: string) => {
    jobs.current.get(id)?.ctrl.abort()
    jobs.current.delete(id)
    setItems((all) => all.filter((a) => a.id !== id))
  }, [])

  const retry = useCallback((id: string) => {
    const a = latest.current.find((x) => x.id === id)
    if (!a) return
    patch(id, { status: 'uploading', progress: 0, error: undefined })
    start(id, a.file)
  }, [start, patch])

  /** Forget the tray without cancelling: used after a send, when every upload has finished. */
  const clear = useCallback(() => { jobs.current.clear(); setItems([]) }, [])

  /** The uploaded paths in tray order, once every file is up; null if one failed or was removed meanwhile. */
  const settle = useCallback(async (ids: string[]): Promise<string[] | null> => {
    const paths = await Promise.all(ids.map((id) => jobs.current.get(id)?.done ?? Promise.resolve(null)))
    return paths.every((p): p is string => !!p) ? paths : null
  }, [])

  // Another session's tray never carries over; uploads still going are dropped.
  useEffect(() => () => { for (const j of jobs.current.values()) j.ctrl.abort(); jobs.current.clear(); setItems([]) }, [sessionId])

  return { items, add, remove, retry, clear, settle }
}
